import {Placement} from '@popperjs/core';
import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled from 'styled-components';

import {InterfaceEventsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipIconWithHover} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipIconWithHover';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {LearnMoreButton} from '../../../../components/common/callToAction/learnMoreButton';
import {EducationTooltipCoordinator} from '../../../../components/common/tooltips/educationTooltip/educationTooltipCoordinator';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getAnalyticsReportType} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';

/*
 * Types.
 */

export interface AnalyticsEducationTooltip<MetricIdType extends string = string> {
  title: ReactNode;
  tooltipContent: ReactNode;
  learnMoreUrl: string;
  calculationExplanation?: ReactNode;
  metricId?: MetricIdType;
}

/*
 * Props.
 */

type AnalyticsSimpleTooltip = {label: string};

interface AnalyticsTooltipsProps {
  tooltip: AnalyticsEducationTooltip | AnalyticsSimpleTooltip | undefined;
  reportSectionId: string;
  placement?: Placement;
  className?: string;
  delay?: number;
  size?: number;
}

/*
 * Component.
 */

export const AnalyticsTooltip: FC<PropsWithChildren<AnalyticsTooltipsProps>> = ({
  tooltip: complexTooltip,
  reportSectionId,
  className,
  placement = 'top',
  delay,
  size,
  children,
}) => {
  const reportType = useAppSelector(getAnalyticsReportType);
  const trackAnalytics = useAnalyticsTracking();

  if (isComplexAnalyticsTooltip(complexTooltip)) {
    const sendTrackingInfoOnClick = () => {
      trackAnalytics(InterfaceEventsEnum.ANALYTICS_TOOLTIP_CTA_CLICK, {
        report_type: reportType,
        report_section: reportSectionId,
        metric_type: complexTooltip.metricId,
      });
    };

    return (
      <EducationTooltipCoordinator
        title={complexTooltip.title}
        tooltip={complexTooltip.tooltipContent}
        placement={placement}
        className={className}
        delay={delay}
        buttons={
          complexTooltip.learnMoreUrl
            ? [
                <LearnMoreButton
                  size={VisualSizesEnum.SMALL}
                  variant={ButtonVariantsEnum.DARK_MODE_LINK}
                  helpPageUrl={complexTooltip.learnMoreUrl}
                  onClick={sendTrackingInfoOnClick}
                />,
              ]
            : undefined
        }
      >
        {children || <TooltipIconWithHover />}
      </EducationTooltipCoordinator>
    );
  }

  return (
    <TooltipCoordinator
      isMultiline
      className={className}
      placement={placement}
      render={() =>
        complexTooltip?.label && <TooltipContentWrapper>{complexTooltip.label}</TooltipContentWrapper>
      }
    >
      {children || <TooltipIconWithHover size={size} />}
    </TooltipCoordinator>
  );
};

/*
 * Helpers.
 */

export function isComplexAnalyticsTooltip(
  tooltip: AnalyticsEducationTooltip | AnalyticsSimpleTooltip | undefined,
): tooltip is AnalyticsEducationTooltip {
  return Boolean(tooltip && typeof tooltip === 'object' && 'tooltipContent' in tooltip);
}

/*
 * Styles.
 */

const TooltipContentWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  size: ${fontSizes.verySmall};

  // For design
  line-height: 18px;
`;
